import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

type SessionRow = {
  player_id: string;
  game_date: string;
  mode: string;
  outcome: string;
  max_level: number | null;
  score: number | null;
};

type DayStats = {
  date: string;
  uniquePlayers: number;
  started: number;
  finished: number;
  won: number;
  lost: number;
  abandoned: number;
};

const RANGES = [7, 14, 30, 90] as const;

export default function AdminStats() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<SessionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState<number>(14);
  const [mode, setMode] = useState<"all" | "daily" | "practice">("daily");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const from = new Date();
        from.setDate(from.getDate() - (days - 1));
        let query = supabase
          .from("game_sessions")
          .select("player_id,game_date,mode,outcome,max_level,score")
          .gte("game_date", from.toISOString().slice(0, 10))
          .order("game_date", { ascending: false })
          .limit(10000);
        if (mode !== "all") query = query.eq("mode", mode);
        const { data, error } = await query;
        if (error) throw error;
        setRows((data ?? []) as SessionRow[]);
      } catch (e: any) {
        toast.error(e.message ?? "Failed to load stats");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [days, mode]);

  const byDay = useMemo<DayStats[]>(() => {
    const map = new Map<string, { players: Set<string> } & Omit<DayStats, "date" | "uniquePlayers">>();
    for (const r of rows) {
      const entry =
        map.get(r.game_date) ??
        { players: new Set<string>(), started: 0, finished: 0, won: 0, lost: 0, abandoned: 0 };
      entry.players.add(r.player_id);
      entry.started += 1;
      if (r.outcome === "won") { entry.won += 1; entry.finished += 1; }
      else if (r.outcome === "lost") { entry.lost += 1; entry.finished += 1; }
      else entry.abandoned += 1;
      map.set(r.game_date, entry);
    }
    return Array.from(map.entries())
      .map(([date, v]) => ({
        date,
        uniquePlayers: v.players.size,
        started: v.started,
        finished: v.finished,
        won: v.won,
        lost: v.lost,
        abandoned: v.abandoned,
      }))
      .sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [rows]);

  const totals = useMemo(() => {
    const players = new Set(rows.map((r) => r.player_id));
    const won = rows.filter((r) => r.outcome === "won").length;
    const lost = rows.filter((r) => r.outcome === "lost").length;
    const abandoned = rows.filter((r) => r.outcome === "started").length;
    const started = rows.length;
    return {
      uniquePlayers: players.size,
      started,
      finished: won + lost,
      won,
      lost,
      abandoned,
      completionRate: started ? Math.round(((won + lost) / started) * 100) : 0,
      winRate: won + lost ? Math.round((won / (won + lost)) * 100) : 0,
    };
  }, [rows]);

  const maxPlayers = Math.max(1, ...byDay.map((d) => d.uniquePlayers));

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#fbf9f4] text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="font-serif text-xl">Player stats</h1>
          <div className="flex gap-2">
            <Link to="/admin" className="text-sm text-slate-600 hover:text-[#de0046] self-center">Blog admin →</Link>
            <Button variant="outline" onClick={signOut}>Sign out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        <div className="flex flex-wrap gap-2">
          {RANGES.map((r) => (
            <Button key={r} size="sm" variant={days === r ? "default" : "outline"}
              className={days === r ? "bg-[#de0046] hover:bg-[#b8003a]" : ""}
              onClick={() => setDays(r)}>
              Last {r} days
            </Button>
          ))}
          <span className="w-px bg-slate-300 mx-2" />
          {(["daily", "practice", "all"] as const).map((m) => (
            <Button key={m} size="sm" variant={mode === m ? "default" : "outline"}
              className={mode === m ? "bg-slate-900 hover:bg-slate-800" : ""}
              onClick={() => setMode(m)}>
              {m === "all" ? "All modes" : m === "daily" ? "Daily" : "Practice"}
            </Button>
          ))}
        </div>

        {loading ? (
          <p>Loading…</p>
        ) : (
          <>
            <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Unique players", value: totals.uniquePlayers },
                { label: "Games started", value: totals.started },
                { label: "Games finished", value: `${totals.finished} (${totals.completionRate}%)` },
                { label: "Abandoned", value: totals.abandoned },
                { label: "Wins", value: totals.won },
                { label: "Losses", value: totals.lost },
                { label: "Win rate", value: `${totals.winRate}%` },
                { label: "Games / player", value: totals.uniquePlayers ? (totals.started / totals.uniquePlayers).toFixed(1) : "0" },
              ].map((s) => (
                <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">{s.label}</p>
                  <p className="text-2xl font-semibold mt-1">{s.value}</p>
                </div>
              ))}
            </section>

            <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200">
                <h2 className="font-medium">Daily breakdown</h2>
              </div>
              {byDay.length === 0 ? (
                <p className="p-4 text-slate-600 text-sm">No sessions recorded in this period yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="text-left px-4 py-2 font-medium">Date</th>
                        <th className="text-right px-4 py-2 font-medium">Unique players</th>
                        <th className="text-right px-4 py-2 font-medium">Started</th>
                        <th className="text-right px-4 py-2 font-medium">Finished</th>
                        <th className="text-right px-4 py-2 font-medium">Won</th>
                        <th className="text-right px-4 py-2 font-medium">Lost</th>
                        <th className="text-right px-4 py-2 font-medium">Abandoned</th>
                        <th className="px-4 py-2" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {byDay.map((d) => (
                        <tr key={d.date}>
                          <td className="px-4 py-2 whitespace-nowrap">{d.date}</td>
                          <td className="px-4 py-2 text-right font-medium">{d.uniquePlayers}</td>
                          <td className="px-4 py-2 text-right">{d.started}</td>
                          <td className="px-4 py-2 text-right">{d.finished}</td>
                          <td className="px-4 py-2 text-right text-emerald-600">{d.won}</td>
                          <td className="px-4 py-2 text-right text-[#de0046]">{d.lost}</td>
                          <td className="px-4 py-2 text-right text-amber-600">{d.abandoned}</td>
                          <td className="px-4 py-2 w-40">
                            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full bg-[#de0046] rounded-full"
                                style={{ width: `${(d.uniquePlayers / maxPlayers) * 100}%` }} />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <p className="text-xs text-slate-500">
              Unique players are counted from an anonymous device id. "Abandoned" means the game was
              started but never finished (no win or loss recorded).
            </p>
          </>
        )}
      </main>
    </div>
  );
}
